import {
  Text,
  Heading,
  Flex,
  DataList,
  Separator,
  ScrollArea,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { schoolGroupColors, useSurveyStore } from "@store/survey";
import { Trans } from "@lingui/macro";
import PropTypes from "prop-types";

const defaultColor = "#8D8D8D";

const percentageFormat = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const integerFormat = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const singleDecimalFormat = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export default function SchoolSummary() {
  const { selectedSchool: feature } = useSurveyStore();
  const [color, setColor] = useState(defaultColor);

  useEffect(() => {
    setColor(
      feature?.properties?.category
        ? schoolGroupColors[feature?.properties?.category]
        : defaultColor,
    );
  }, [feature]);

  return (
    <Flex gap="1" direction="column">
      <Heading as="h2" size="4">
        {feature?.properties?.school_name}
      </Heading>
      <Heading
        size="3"
        as="h3"
        style={{
          color: color,
        }}
      >
        {feature?.properties?.category}
      </Heading>
      <Heading
        size="2"
        as="h4"
        style={{
          color: color,
        }}
        weight="regular"
      >
        {feature?.properties?.strategy_candidate_group}
      </Heading>

      <Separator orientation="horizontal" size="4" mt="4" mb="1" />
      <Text size="2" color="gray" mb="2">
        <Trans id="SchoolSummary.key_metrics">Key metrics</Trans>
      </Text>
      <ScrollArea type="always" scrollbars="vertical" style={{ height: 180 }}>
        <DataList.Root size="1">
          <DataListItem value={feature?.properties?.school_type}>
            <Trans>School type</Trans>
          </DataListItem>
          <DataListItem
            value={feature?.properties?.enrollment_sy2324}
            numberFormatting={integerFormat}
          >
            <Trans>Enrollment (SY 23-24)</Trans>
          </DataListItem>
          <DataListItem
            value={feature?.properties?.capacity}
            numberFormatting={integerFormat}
          >
            <Trans>Capacity</Trans>
          </DataListItem>
          <DataListItem
            value={feature?.properties?.utilization}
            numberFormatting={percentageFormat}
          >
            <Trans>Utilization rate</Trans>
          </DataListItem>
          <DataListItem
            value={parseFloat(feature?.properties?.enrollment_change_5yrs)}
            numberFormatting={percentageFormat}
          >
            <Trans>Enrollment change over past 5 years</Trans>
          </DataListItem>
          <DataListItem
            value={feature?.properties?.enrollment_projected_10yr}
            numberFormatting={integerFormat}
          >
            <Trans>Projected 10-year enrollment</Trans>
          </DataListItem>
          <DataListItem
            value={feature?.properties?.utilization_projected_10yr}
            numberFormatting={percentageFormat}
          >
            <Trans>Projected 10-year utilization rate</Trans>
          </DataListItem>
          <DataListItem value={feature?.properties?.underutilized_schools_1mi}>
            <Trans>Within 1 mile of underutilized school(s)</Trans>
          </DataListItem>
          <DataListItem value={feature?.properties?.capacity_schools_1mi}>
            <Trans>
              School(s) within 1 mile have available student capacity
            </Trans>
          </DataListItem>
          <DataListItem
            value={
              feature?.properties?.composite_score &&
              feature?.properties?.composite_score !== "N/A"
                ? singleDecimalFormat
                    .format(parseFloat(feature?.properties?.composite_score))
                    .toString() + " / 10"
                : "N/A"
            }
          >
            <Trans>Composite Building Score</Trans>
          </DataListItem>
          <DataListItem value={feature?.properties?.specialty_programs_2plus}>
            <Trans>2 or more specialty program offerings</Trans>
          </DataListItem>
          <DataListItem value={feature?.properties?.capacity_campus_expansion}>
            <Trans>Site capacity for campus expansion</Trans>
          </DataListItem>
        </DataList.Root>
      </ScrollArea>
    </Flex>
  );
}

DataListItem.propTypes = {
  children: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  numberFormatting: PropTypes.object,
};

function DataListItem({ value, numberFormatting, children }) {
  return (
    <DataList.Item align="start">
      <DataList.Label width="150px">{children}</DataList.Label>
      <DataList.Value>
        {value ? (
          numberFormatting && typeof value === "number" ? (
            <Text size="1">{numberFormatting.format(value)}</Text>
          ) : (
            <Text size="1">{value}</Text>
          )
        ) : (
          <Text size="1">
            <Trans id="SchoolSummary.no_data">N/A</Trans>
          </Text>
        )}
      </DataList.Value>
    </DataList.Item>
  );
}
